<!-- 
* WHAT: The user interface for building model in BtChartForLS component.
* HISTORY:
- V260105.1: Consumed the has-filter prop in bt-metrics-model and removed it from bt-dims-metrics-model, bt-dates-model, and bt-histogram-metrics-model.
- V251204.1: Added isCalculatedTable prop and used it to limit models selections to *-metrics only.
- V251120.1: Added isPaginatedTable prop and consumed initial-column prop of BtColumnDistinctModel + Hid model dropdown when there was one item only + Fixed an issue that didn't disable/enable save btn properly.
- V251027.2: 1st Release.
-->
<template>
<div>
   <v-textarea outlined readonly
      class="pt-3 pr-0"
      label="* Model in JSON Format: "
      rows="3"
      append-icon="edit"
      :rules="[rules.required]"
      v-model="serializedModelData"
      @click:append="editSerializedModelData"
   ></v-textarea>

   <v-dialog persistent no-click-animation scrollable
      max-width="1280px"
      v-model="dialog"
   >
      <v-card class="px-4 pb-2">
         <v-card-title class="title grey--text darken-4 font-weight-bold pl-3 pt-3 pb-0">Model Builder{{isPaginatedTable ? ' for Table with Pagination' : ''}}:</v-card-title>
         <v-container>
            <v-form lazy-validation
               ref="form"
               v-model="isFormValid"
            >
               <v-row v-show="modelItems.length > 1"><!--V251120-->
                  <v-col xs="12" md="4" class="py-2">
                     <v-select dense persistent-hint hide-selected required
                        ref="model"
                        placeholder="select a model..."
                        hint="* Model"
                        :items="modelItems"
                        :rules="[rules.required]"
                        v-model="model"
                        @change="modelChanged"
                     ></v-select>
                  </v-col>
               </v-row>
               <v-row v-if="schemas?.length && model">
                  <v-col cols="12">
                     <bt-metrics-model v-if="model === models.metrics"
                        has-filter
                        :initial-model="initialModel"
                        :schemas="schemas"
                        :query-submitted="isModelDataValid"
                        v-model="modelData"
                        @form-valid="modelDataChanged"
                     />
                        <!--V260105: has-filter -->
                     <bt-dims-metrics-model v-else-if="model === models.dims"
                        :initial-model="initialModel"
                        :schemas="schemas"
                        :query-submitted="isModelDataValid"
                        v-model="modelData"
                        @form-valid="modelDataChanged"
                     />
                     <bt-dims-dim-model v-else-if="model === models.dimsDim"
                        :initial-model="initialModel"
                        :schemas="schemas"
                        :query-submitted="isModelDataValid"
                        :pivot-dim-in-items="pivotDimInItems"
                        v-model="modelData"
                        @form-valid="modelDataChanged"
                        @pivot-dim-change="pivotDimChanged"
                     />
                        <!--V260105: has-filter -->
                     <bt-dates-model v-else-if="model.startsWith('date-')"
                        :initial-model="initialModel"
                        :type="model"
                        :schemas="schemas"
                        :query-submitted="isModelDataValid"
                        :pivot-dim-in-items="pivotDimInItems"
                        v-model="modelData"
                        @form-valid="modelDataChanged"
                        @pivot-dim-change="pivotDimChanged"
                     />
                        <!--V260105: has-filter -->
                     <bt-histogram-metrics-model v-else-if="model === models.histogram"
                        :initial-model="initialModel"
                        :schemas="schemas"
                        :query-submitted="isModelDataValid"
                        v-model="modelData"
                        @form-valid="modelDataChanged"
                     />
                     <bt-column-distinct-model v-else-if="model === models.column"
                        :initial-column="isPaginatedTable ? '_id' : undefined"
                        :initial-model="initialModel"
                        :schemas="schemas"
                        :query-submitted="isModelDataValid"
                        v-model="modelData"
                        @form-valid="modelDataChanged"
                     />
                  </v-col>
               </v-row>
            </v-form>
         </v-container>
         <v-card-actions class="py-0 pr-0">
            <div class="flex-grow-1"></div>
            <v-btn text
               class="px-0"
               color="blue darken-1"
               @click="cancelClicked"
            >Cancel</v-btn>
            <v-btn text
               class="px-0 ml-0"
               color="blue darken-1"
               :disabled="isSaveDisabled"
               @click="saveClicked"
            >Save</v-btn>
         </v-card-actions>
      </v-card>
   </v-dialog>
</div>
</template>

<script>
const NAME = "BtModelBuilder";
const MSG = `-----${NAME} V260105.1 says => `;
const MODELS = {
   metrics: 'metrics',
   dims: 'dims-metrics',
   dimsDim: 'dims-dim',
   date: 'date-metrics',
   dateDate: 'date-date',
   dateDim: 'date-dim',
   histogram: 'histogram-metrics',
   column: 'column-distinct'
}

import BtMetricsModel from './BtMetricsModel.vue';
import BtDimsMetricsModel from './BtDimsMetricsModel.vue';
import BtDimsDimModel from './BtDimsDimModel.vue';
import BtDatesModel from './BtDatesModel.vue';
import BtHistogramMetricsModel from './BtHistogramMetricsModel.vue';
import BtColumnDistinctModel from './BtColumnDistinctModel.vue';
import { isEmpty, isLocal, omitKeys, sortedStringify } from '../mixins/bt-mixin.js';

export default {
   name: NAME,

   components: {
      BtMetricsModel,
      BtDimsMetricsModel,
      BtDimsDimModel,
      BtDatesModel,
      BtHistogramMetricsModel,
      BtColumnDistinctModel
   },

   props: {
      value: {
         required: true,
         type: String,
         default: ''
      },
      debug: {
         type: Boolean,
         default: false
      },
      isDropdown: {
         type: Boolean,
         default: false
      },
      isCalculatedTable: {  //V251204
         type: Boolean,
         default: false
      },
      isPaginatedTable: {  //V251120
         type: Boolean,
         default: false
      },
      lake: {
         required: true,
         type: Object,
         default: () => {}
      },
      pivotDimInItems: {
         type: Array,
         default: () => []
      },
      schemas: {
         type: Array,
         required: true,
         default: () => []
      }
   },

   data() {
      return {
         serializedModelData: '',
         modelData: {},
         isModelDataValid: false,
         dialog: false,
         isFormValid: false,
         rules: {
            required: value => !!value || 'Value is required!',
         },
         modelItems: [],
         model: '',
         initialModel: undefined,
         // isFormChanged: false
      }
   },

   computed: {
      models: () => MODELS,

      isSaveDisabled() {
         // const t1 = this.isFormChanged;
         // const t2 = this.isFormValid;
         // const t3 = this.isModelDataValid;
         // const t4 = !isEmpty(this.modelData);
         // // alert(`FormChanged=${t1}, FormValid=${t2}, ModelDataValid=${t3}, isEmpty=${t4}`);
         const result = !this.isFormChanged || !this.isFormValid || !this.isModelDataValid || isEmpty(this.modelData);
         // alert('isFormChanged=' + this.isFormChanged + '\nisFormValid=' + this.isFormValid + '\nisModelDataValid=' + this.isModelDataValid + '\nmodelData=' + isEmpty(this.modelData));
         return result;
      },

      isFormChanged() {
         const currValue = sortedStringify({
            type: this.model,
            ...this.modelData
         });
         // alert(this.inValue+'\n' + currValue)
         return this.inValue !== currValue;
      }
   },

   watch: {
      value: {
         immediate: true,
         deep: true,
         handler(val) {
            // alert('in watch.value(): val=' + val);
            this.serializedModelData = val;//isEmpty(val) ? '' : JSON.stringify(val);
            // this.$forceUpdate();
         }
      }
   },

   methods: {
      log(msg, isError) {
         if (isError)
            console.error(`${MSG}${msg}`);
         else if (this.debug) {
            console.log(`${MSG}${msg}`);
            // alert(`${MSG}${msg}`);
         }
      },

      editSerializedModelData() {
         // this.modelData = this.serializedModelData ? JSON.parse(this.serializedModelData) : {};
         // const parsedData = this.serializedModelData ? omitKeys(JSON.parse(this.serializedModelData), ['type', 'lake']) : {};

         // this.isFormChanged = false;
         this.initialModel = true;

         if (this.isDropdown || this.isPaginatedTable)
            this.modelItems = [
               { text: 'Column - Distinct', value: MODELS.column }
            ];
         else if (this.isCalculatedTable)
            this.modelItems = [
               { text: 'Dimensions - Metrics', value: MODELS.dims },
               { text: 'Date - Metrics', value: MODELS.date },
               { text: 'Histogram - Metrics', value: MODELS.histogram }
            ];
         else
            this.modelItems = [
               { text: 'Metrics', value: MODELS.metrics },
               { text: 'Dimensions - Metrics', value: MODELS.dims },
               { text: 'Dimensions - Dimension', value: MODELS.dimsDim },
               { text: 'Date - Metrics', value: MODELS.date },
               { text: 'Date - Date', value: MODELS.dateDate },
               { text: 'Date - Dimension', value: MODELS.dateDim },
               { text: 'Histogram - Metrics', value: MODELS.histogram }
            ];

         
         if (this.serializedModelData) {
            const parsedData = JSON.parse(this.serializedModelData);
            this.model = parsedData.type;
            this.modelData =  omitKeys(parsedData, ['type', 'lake']);

            // alert('modelData=' + JSON.stringify(this.modelData));
            this.isModelDataValid = true;
         } else {
            this.model = this.modelItems.length === 1 ? this.modelItems[0].value : '';
            this.modelData =  {};
            this.isModelDataValid = false;
         }

         this.inValue = sortedStringify({
            type: this.model,
            ...this.modelData
         });

         this.$nextTick(() => {
            this.dialog = true;
         });
      },

      modelChanged() {
         // if (isLocal) {
         //    this.modelData = this.initialMetrics();
         // }
         this.query = {};
         this.queryResult = [];
         // this.pivotDimInItems = [];
         this.chartData = [];
         this.initialModel = true;
         // this.isFormChanged = true;
         this.isModelDataValid = false;
      },

      modelDataChanged(val) {
         // alert('modelDataChanged:' + val)
         this.isModelDataValid = val;
         // this.isFormChanged = true;
      },

      async pivotDimChanged(pivotDim) {
         this.$emit('pivot-dim-change', pivotDim);
      },

      cancelClicked() {
         this.initialModel = undefined;
         this.$refs.form.resetValidation();
         this.dialog = false;
      },

      saveClicked() {
         if (this.isFormValid && this.isModelDataValid && !isEmpty(this.modelData)) {
            this.serializedModelData = JSON.stringify({
               type: this.model,
               ...this.modelData,
               lake: this.lake
            });
            this.$emit('input', this.serializedModelData);
            this.$emit('change', this.serializedModelData);
            this.cancelClicked();
         }
      },

      initialMetrics() {
         if (!isLocal) return undefined;

         let modelData;
         switch (this.model) {
            case MODELS.metrics:
               modelData = {
                  metrics: [
                     { metric: 'count', on: '*', label: 'Count' },
                     { metric: 'sum', on: 'price', label: 'Total' },
                     { metric: 'max', on: 'price', label: 'Maximum' },
                     { metric: 'min', on: 'price', label: 'Minimum' }
                  ]
               };
               break;
            case MODELS.dims:
               modelData = {
                  dims: ['Countries','State','month(event_date) as event_date_month', "STRFTIME(CAST(event_date as DATE), '%Y-%m-%d') as event_date_date"],
                  // dims: ['Countries','State','month(event_date) as event_date_month', "STRFTIME(CAST(event_date as DATE), '%Y-%m-%d') as event_date_date"],
                  // orderBy: 'Countries asc,State asc,event_date_month asc',
                  orderBy: 'Countries asc,State asc,event_date_month asc,event_date_date asc',
                  whereClause: "where Countries = 'United States'"
               };
               break;
            case MODELS.dimsDim:
               modelData = {
                  groupByDims: ['Product'],
                  orderBy: 'Product asc',
                  // pivotDim: "Payment_Type",
                  // pivotDimIn: [ "Amex", "Mastercard" ],
                  pivotDim: "event_code",
                  pivotDimIn: [ "Sales", "70111", "70110" ],
                  pivotDimQuote: true
               };
               break;
            case MODELS.date:
            case MODELS.dateDate:
               modelData = {
                  dateField: "Account_Created",
                  dateParts: [ "year", "quarter" ]
               };
               break;
            case MODELS.dateDim:
               modelData = {
                  dateField: "Account_Created",
                  dateParts: [ "year", "quarter" ],
                  pivotDim: "Product",
                  pivotDimIn: [ "Product1", "Product2" ],
                  pivotDimQuote: true
               };
               break;
            case MODELS.histogram:
               modelData = {
                  histogramOn: "price",
                  histogramRange: [ 1000, 10000 ],
                  histogramStep: 1000,
                  histogramLable: "PriceRange"
               };
               break;
            case MODELS.column:
               return {
                  pivotDim: "Product"
               };
            default:
               modelData = {};
               break;
         }

         modelData.metrics = modelData.metrics || [{
            metric: 'count',
            on: '*',
            label: 'Count'
         }];

         return modelData;
      }
   },

   created() {
      // alert(`in ${NAME}.create()`);
   }
}
</script>
